// Every stylesheet, imported once, here (checklist §3).
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import 'mantine-datatable/styles.css';
// MUST come after all @mantine/*/styles.css imports — overrides depend on it.
import '@flexinet/ui-components/style.css';

import {ReactNode} from 'react';
import {createBrowserRouter, RouterProvider} from 'react-router-dom';
import {QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {
    createAuthAwareQueryClient,
    SessionGuardProvider,
    useVerifiedPermissions,
    VerifiedPermissionsProvider,
} from '@flexinet/auth-components';
import {Alert, MantineProvider} from '@mantine/core';
import {Notifications} from '@mantine/notifications';
import {ModalsProvider} from '@mantine/modals';
import {AnalyticsAuthListener} from '@flexinet/analytics-components';

import {AuthProvider} from './providers/AuthProvider';
import {UserAttributesProvider} from './providers/UserAttributesProvider';
import {UserAccountProvider} from './providers/UserAccountProvider';
import {useAuth} from './hooks/useAuth';
import {actions} from './config/rbac/actions';
import permissions from './config/permissions';
import {routesConfig} from './config/routes';
import {theme} from './theme';

// FNP-1725: the shared provider from @flexinet/auth-components. It reads no
// AuthContext of its own, so the session comes from this app's: it asks AVP
// nothing until `status` is 'authenticated', and asks again when the signed-in
// user changes. The skeleton's old local copy called AVP on mount whatever the
// session, logged "idToken is required" on every signed-out load, and never
// re-ran after sign-in. Must sit inside <AuthProvider>.
//
// Interim until FNP-1749. The shared provider keeps a failed AVP call in
// context as {status: 'failed'} and shows nothing, so a wrong namespace or a
// rejected token would get an empty navbar with no explanation. One line in the app's usual error Alert, nothing more.
const PermissionsFailureNotice = () => {
    const {status} = useVerifiedPermissions();
    return status === 'failed' ? <Alert color={'red'}>We couldn't check your permissions.</Alert> : null;
};

export const PermissionsFromSession = ({children}: {children: ReactNode}) => {
    const {status, user} = useAuth();
    return (
        <VerifiedPermissionsProvider
            actions={actions}
            policyStoreId={permissions.policyStoreId}
            region={permissions.region}
            actionType={permissions.actionType}
            enabled={permissions.enabled}
            sessionStatus={status}
            sessionKey={status === 'authenticated' ? user?.userId : undefined}
        >
            <PermissionsFailureNotice/>
            {children}
        </VerifiedPermissionsProvider>
    );
};

// ⚠️ BOTH OF THESE ARE THE SESSION LAYER, AND THEY ARE ONE THING, NOT TWO.
//
// The skeleton used to build a bare `new QueryClient({...})` here and mount no
// SessionGuardProvider at all. That combination is the single highest-value
// box on the application compliance checklist, and failing it produces a
// symptom that reads as a React Query bug: queries do not retry after a token
// refresh, so the app shows stale or empty data until a manual reload.
//
// `createAuthAwareQueryClient()` is what ties query retries to the session,
// and `SessionGuardProvider` is what owns refresh and expiry. Every real
// application on the platform — console, domains, billing — mounts both.
// Replacing either with a hand-rolled equivalent reintroduces the defect.
const router = createBrowserRouter(routesConfig);
const queryClient = createAuthAwareQueryClient();

export const App = () => {
    return (
        <MantineProvider theme={theme}>
            <Notifications position="top-right"/>
            <SessionGuardProvider>
                <QueryClientProvider client={queryClient}>
                    <AuthProvider>
                        <AnalyticsAuthListener/>
                        <UserAttributesProvider>
                            <UserAccountProvider>
                                <PermissionsFromSession>
                                    <ModalsProvider>
                                        {/*
                                          * ⚠️ RouterProvider, not <BrowserRouter> +
                                          * useRoutes. `errorElement` in routes.tsx
                                          * only works under a data router, and
                                          * without it a render error blanks the
                                          * page with the reason in the console.
                                          */}
                                        <RouterProvider router={router}/>
                                    </ModalsProvider>
                                </PermissionsFromSession>
                            </UserAccountProvider>
                        </UserAttributesProvider>
                    </AuthProvider>
                    {/*
                      * ⚠️ INSIDE QueryClientProvider. The devtools call
                      * useQueryClient() in development mode only, so a sibling
                      * placement blanks `npm run dev` while every build and
                      * test stays green (FNP-1715). AppRenders.test.tsx guards it.
                      */}
                    <ReactQueryDevtools initialIsOpen={false}/>
                </QueryClientProvider>
            </SessionGuardProvider>
        </MantineProvider>
    );
};
